import React,{useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import BeltIcon from './BeltIcon';
import KoreaFlag from './symbols/KoreaFlag';
import {FACTS,SOURCES,TOPICS} from '../data/didYouKnow';
import {beltLabel} from '../data/belts';

const {FiRefreshCw,FiExternalLink}=FiIcons;

const randomIndex=(exclude)=> {
  if (FACTS.length<2) return 0;
  let index;
  do {
    index=Math.floor(Math.random()*FACTS.length);
  } while (index===exclude);
  return index;
};

// Tarjeta "¿Sabías que...?": muestra un dato al azar y permite pedir otro.
export default function DidYouKnow({className=''}) {
  const [index,setIndex]=useState(()=> randomIndex(-1));
  const fact=FACTS[index];
  const source=SOURCES[fact.source];

  return (
    <section className={`max-w-7xl mx-auto px-6 py-12 ${className}`}>
      <div className="glass-card rounded-[2.5rem] p-8 md:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-48 h-48 bg-red-600/10 blur-3xl rounded-full -ml-24 -mt-24" />

        <div className="relative flex items-center justify-between gap-4 mb-6">
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tighter text-white [text-shadow:_0_2px_10px_rgba(0,0,0,0.5)]">
            ¿Sabías que...?
          </h2>
          <button
            type="button"
            onClick={()=> setIndex(current=> randomIndex(current))}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-red-600 text-white text-[10px] font-black uppercase tracking-widest transition-colors flex-shrink-0"
          >
            <SafeIcon icon={FiRefreshCw} /> Otro dato
          </button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{opacity: 0,y: 10}}
            animate={{opacity: 1,y: 0}}
            exit={{opacity: 0,y: -10}}
            transition={{duration: 0.25}}
            className="relative flex flex-col md:flex-row gap-8 md:items-center"
          >
            <FactVisual visual={fact.visual} />

            <div className="flex-1 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[9px] font-black uppercase tracking-[0.25em] text-white bg-red-600 px-3 py-1 rounded-full shadow-lg shadow-red-600/20">
                  {TOPICS[fact.topic]}
                </span>
              </div>

              <p className="text-white text-lg md:text-xl leading-relaxed font-medium [text-shadow:_0_1px_4px_rgba(0,0,0,0.6)]">
                {fact.text}
              </p>

              {source.author && (
                <p className="flex items-center gap-2 text-white text-sm font-black [text-shadow:_0_1px_4px_rgba(0,0,0,0.6)]">
                  <span className="h-px w-6 bg-red-500" /> Por {source.author}
                </p>
              )}

              {source.cite && (
                <p className="text-white/70 text-[11px] font-semibold">
                  Fuente:{' '}
                  <a href={source.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-red-400 inline-flex items-center gap-1">
                    {source.label} <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                  </a>
                </p>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

function FactVisual({visual}) {
  if (!visual) return null;

  if (visual==='korea-flag') {
    return (
      <figure className="w-full md:w-72 flex-shrink-0">
        <KoreaFlag className="w-full rounded-xl shadow-xl border border-white/10" />
        <figcaption className="mt-2 text-center text-[9px] font-black uppercase tracking-[0.25em] text-white/70">Tae Kuk Gi</figcaption>
      </figure>
    );
  }

  if (visual==='mexico-colors') {
    return (
      <figure className="w-full md:w-72 flex-shrink-0">
        <div className="grid grid-cols-3 rounded-xl overflow-hidden shadow-xl border border-white/10 h-32">
          <div style={{backgroundColor: '#006847'}} />
          <div style={{backgroundColor: '#FFFFFF'}} />
          <div style={{backgroundColor: '#CE1126'}} />
        </div>
        <figcaption className="mt-2 grid grid-cols-3 text-center text-[9px] font-black uppercase tracking-[0.2em] text-white/70">
          <span>Verde</span><span>Blanco</span><span>Rojo</span>
        </figcaption>
      </figure>
    );
  }

  if (visual==='ammdk-shield') {
    return (
      <figure className="w-full md:w-56 flex-shrink-0 flex flex-col items-center">
        <div className="h-44 w-44 rounded-3xl bg-white/95 p-3 shadow-xl">
          <img src="/assets/brand/logo-ammdk.webp" alt="Escudo de la Asociación Mexicana MDK (Kwan Gi)" className="h-full w-full object-contain" />
        </div>
        <figcaption className="mt-2 text-[9px] font-black uppercase tracking-[0.25em] text-white/70">Kwan Gi · AMMDK</figcaption>
      </figure>
    );
  }

  if (visual.startsWith('belt:')) {
    const belt=visual.slice(5);
    return (
      <figure className="w-full md:w-56 flex-shrink-0 flex flex-col items-center">
        <BeltIcon type={belt} className="w-40 h-12 shadow-xl" />
        <figcaption className="mt-3 text-[9px] font-black uppercase tracking-[0.25em] text-white/70">{beltLabel(belt)}</figcaption>
      </figure>
    );
  }

  return null;
}
